import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import { CheckIcon, ExternalLink, Loader2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from 'recharts';

import { track } from '@repo/analytics';
import { Article, Metrics } from '@repo/types';
import { ShareIcon } from '@repo/ui/components/icons/share-icon';
import { Button } from '@repo/ui/components/ui/button';
import { ChartConfig, ChartContainer } from '@repo/ui/components/ui/chart';
import { Separator } from '@repo/ui/components/ui/separator';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';
import { useRelativeDate } from '@repo/ui/hooks/use-relative-date';
import { cn } from '@repo/ui/lib/utils';

import { useResponsiveRadialChartDimensions } from '@/lib/chart-utils';
import { useArticleShare } from '@/hooks/use-article-share';
import { usePlatform } from '@/hooks/use-platform';
import { HACKER_NEWS_BRAND_ITEM } from '@/components/app-navigator';
import {
  AnalysisTooltipContent,
  useAnalysisContainer,
} from '@/components/article-analysis/analysis-container';

// import { CommentsBottomSheet } from './comments-bottom-sheet';

// Constants
const BRANDS_WITH_SMALL_LOGO = ['ABC News', 'Al Jazeera (Qatar)'] as const;
const CHART_START_ANGLE = 90;
const CHART_ANGLE_MULTIPLIER = 3.6;
const TOOLTIP_DELAY_DURATION = 200;

const CHART_CONFIG = {
  score: {
    label: 'Score',
  },
  notCredible: {
    label: 'Not Credible',
    color: 'hsl(var(--neutral-4))',
  },
  credible: {
    label: 'Credible',
    color: 'hsl(var(--facts-pie-chart))',
  },
} satisfies ChartConfig;

interface ArticleCarouselItemProps {
  index: number;
  article: Article;
  metrics?: Metrics | null;
  selected: number;
  onSelect: (index: number) => void;
  isHackerNews: boolean;
  shouldOpenComments?: boolean;
}

interface BrandLogoProps {
  brand: Article['brand'];
  isHackerNews: boolean;
}

const BrandLogo = memo<BrandLogoProps>(({ brand, isHackerNews }) => {
  const isSmallLogo = useMemo(
    () =>
      brand?.brand
        ? BRANDS_WITH_SMALL_LOGO.includes(
            brand.brand as (typeof BRANDS_WITH_SMALL_LOGO)[number],
          )
        : false,
    [brand?.brand],
  );

  const brandName = useMemo(() => {
    return isHackerNews ? 'Hacker News' : brand?.brand;
  }, [brand?.brand, isHackerNews]);

  const brandLogoUrl = useMemo(() => {
    return isHackerNews
      ? HACKER_NEWS_BRAND_ITEM.logoLargeUrl
      : brand?.logoLargeUrl;
  }, [brand?.logoLargeUrl, isHackerNews]);

  if (!brandLogoUrl) {
    return (
      <span className="text-left text-[10px] font-semibold leading-none text-neutral-2 @cred-md:text-sm @cred-md:leading-4 @cred-xl:text-base @cred-xl:leading-5">
        {brandName}
      </span>
    );
  }

  return (
    <div className="max-w-full shrink min-w-0">
      <Image
        src={brandLogoUrl}
        alt={brandName ?? ''}
        width={300}
        height={48}
        unoptimized
        priority
        className={cn(
          'block size-auto object-contain',
          isSmallLogo
            ? 'max-h-[18px] @cred-md:max-h-[20px] @cred-xl:max-h-[27px]'
            : 'max-h-3.5 @cred-md:max-h-[16.33px] @cred-xl:max-h-[23px]',
        )}
      />
    </div>
  );
});

BrandLogo.displayName = 'BrandLogo';

interface ArticleHeaderProps {
  brand: Article['brand'];
  publishedAt: string;
  createdAt: string;
  isHackerNews: boolean;
  isSharingUrlPending: boolean;
  isSharingUrlCopied: boolean;
  handleShare: () => void;
}

const ArticleHeader = memo<ArticleHeaderProps>(
  ({
    brand,
    publishedAt,
    createdAt,
    isHackerNews,
    isSharingUrlPending,
    isSharingUrlCopied,
    handleShare,
  }) => {
    const formattedDate = useRelativeDate(
      !publishedAt
        ? null
        : isHackerNews
          ? publishedAt || createdAt
          : publishedAt,
    );

    return (
      <div className="flex h-[15px] w-full items-center gap-1 px-2 @cred-md:px-[9.33px] @cred-xl:px-[13.33px] text-[#757575] @cred-md:h-[18px] @cred-lg:h-[19px] @cred-xl:h-[25px]">
        <BrandLogo brand={brand} isHackerNews={isHackerNews} />
        {formattedDate && (
          <>
            <span className="shrink-0 text-[10px] leading-[15px] @cred-md:text-sm @cred-md:leading-4 @cred-lg:text-[15.2px] @cred-lg:leading-[18px] @cred-xl:text-xl @cred-xl:leading-[23px]">
              •
            </span>
            <span className="shrink-0 text-[10px] leading-[15px] @cred-md:text-xs @cred-md:leading-[18px] @cred-lg:text-[13px] @cred-lg:leading-[19px] @cred-xl:text-base @cred-xl:leading-[25px]">
              {formattedDate}
            </span>
          </>
        )}
        <div className="ml-auto shrink-0 flex items-center">
          <Button
            size="icon"
            variant="ghost"
            disabled={isSharingUrlPending}
            onClick={(e) => {
              e.stopPropagation();
              handleShare();
            }}
            className="size-5 stroke-[1.7px] text-black [&_svg]:size-[15px]"
          >
            <AnimatePresence mode="wait">
              {isSharingUrlPending ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                  }}
                  transition={{ duration: 0.05, ease: 'easeInOut' }}
                >
                  <Loader2 className="animate-spin" />
                </motion.div>
              ) : isSharingUrlCopied ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                    transition: { duration: 0.08, ease: 'easeInOut' },
                  }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <CheckIcon />
                </motion.div>
              ) : (
                <motion.div
                  key="share"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                    transition: { duration: 0.08, ease: 'easeInOut' },
                  }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <ShareIcon />
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </div>
      </div>
    );
  },
);

ArticleHeader.displayName = 'ArticleHeader';

interface CredibilityChartProps {
  chartData: Array<{ type: string; score: number; fill: string }>;
  endAngle: number;
  selected: number;
  index: number;
}

const CredibilityChart = memo<CredibilityChartProps>(
  ({ chartData, endAngle, selected, index }) => {
    const { isWebkit } = usePlatform();
    const chartDimensions = useResponsiveRadialChartDimensions(
      'small',
      useAnalysisContainer(),
    );

    const isSelected = selected === index;
    const score = chartData[0]?.score ?? 0;

    return (
      <div className="size-[54px] shrink-0 self-center @cred-md:size-[63px] @cred-lg:size-[68px] @cred-xl:size-[91.7px]">
        <ChartContainer
          config={CHART_CONFIG}
          className="aspect-square max-h-[54px] w-full @cred-md:max-h-[63px] @cred-lg:max-h-[68px] @cred-xl:max-h-[91.7px]"
        >
          <RadialBarChart
            data={chartData}
            startAngle={CHART_START_ANGLE}
            endAngle={endAngle}
            innerRadius={chartDimensions.innerRadius}
            outerRadius={chartDimensions.outerRadius}
          >
            <PolarGrid
              gridType="circle"
              radialLines={false}
              stroke="none"
              className={cn(
                'transition-colors duration-200 first:fill-[#CDD9FF]',
                isSelected ? 'last:fill-neutral-1' : 'last:fill-silver-1',
              )}
              polarRadius={chartDimensions.polarRadius}
            />
            <RadialBar dataKey="score" background cornerRadius={10} />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                formatter={(value: number) => `${value}%`}
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={isWebkit ? (viewBox.cy || 0) + 6 : viewBox.cy || 0}
                          className="fill-neutral-2 text-base font-bold leading-[19px] @cred-md:text-lg @cred-md:leading-[21px] @cred-lg:text-xl @cred-lg:leading-[23px] @cred-xl:text-[26px] @cred-xl:leading-[30px]"
                        >
                          {score}%
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </PolarRadiusAxis>
          </RadialBarChart>
        </ChartContainer>
      </div>
    );
  },
);

CredibilityChart.displayName = 'CredibilityChart';

interface ActionButtonsProps {
  article: Article;
  isSharingUrlPending: boolean;
  isSharingUrlCopied: boolean;
  handleShare: () => void;
  shouldOpenComments?: boolean;
}

const ActionButtons = memo<ActionButtonsProps>(
  ({
    // article,
    isSharingUrlPending,
    isSharingUrlCopied,
    handleShare,
    // TODO: uncomment once we have comments fully implemented
    // shouldOpenComments = false,
  }) => {
    // const hasComments = useMemo(
    //   () =>
    //     article.aiGeneratedComments && article.aiGeneratedComments?.length > 0,
    //   [article.aiGeneratedComments],
    // );

    // const [isCommentsOpen, setIsCommentsOpen] = useState(shouldOpenComments);

    // useEffect(() => {
    //   if (shouldOpenComments) {
    //     const timeoutId = setTimeout(() => {
    //       setIsCommentsOpen(true);
    //     }, 300);
    //     return () => clearTimeout(timeoutId);
    //   }
    // }, [shouldOpenComments]);

    return (
      <div className="mt-auto! w-full flex flex-col">
        <div className="border-t border-[#C0C0C0] h-[27px] bg-transparent flex items-center w-full @cred-md:h-[29px] @cred-lg:h-[30px] @cred-xl:h-[38px]">
          <Button
            disabled={isSharingUrlPending}
            onClick={(e) => {
              e.stopPropagation();
              handleShare();
            }}
            className="h-[27px] shadow-none w-full border-none rounded-b-sm rounded-t-none bg-transparent hover:bg-black/10 p-0 text-[10px] font-medium leading-[15.3504px] text-black hover:text-black @cred-md:text-[11px] @cred-md:leading-4 @cred-lg:text-[13px] @cred-lg:leading-[17px] @cred-xl:text-sm @cred-xl:leading-[19px] [&_svg]:size-3 @cred-md:[&_svg]:size-3.5 @cred-lg:[&_svg]:size-4 @cred-xl:[&_svg]:size-4.5 @cred-md:h-[29px] @cred-lg:h-[30px] @cred-xl:h-[38px] stroke-[1.7px]"
          >
            <AnimatePresence mode="wait">
              {isSharingUrlPending ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                  }}
                  transition={{ duration: 0.05, ease: 'easeInOut' }}
                >
                  <Loader2 className="animate-spin" />
                </motion.div>
              ) : isSharingUrlCopied ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                    transition: { duration: 0.08, ease: 'easeInOut' },
                  }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <CheckIcon />
                </motion.div>
              ) : (
                <motion.div
                  key="share"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.8,
                    transition: { duration: 0.08, ease: 'easeInOut' },
                  }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <ShareIcon />
                </motion.div>
              )}
            </AnimatePresence>
            Share
          </Button>
          {/* <Separator
            orientation="vertical"
            className="h-[27px] bg-[#C0C0C0] mx-0 @cred-md:h-[29px] @cred-lg:h-[30px] @cred-xl:h-[38px]"
          /> */}
          {/* <Button
            onClick={(e) => {
              e.stopPropagation();
              setIsCommentsOpen(true);
            }}
            className="relative h-[27px] shadow-none w-full border-none rounded-b-sm rounded-t-none bg-transparent hover:bg-black/10 p-0 text-[10px] font-medium leading-[15.3504px] text-black hover:text-black @cred-md:text-[11px] @cred-md:leading-4 @cred-lg:text-[13px] @cred-lg:leading-[17px] @cred-xl:text-sm @cred-xl:leading-[19px] [&_svg]:size-3 @cred-md:[&_svg]:size-3.5 @cred-lg:[&_svg]:size-4 @cred-xl:[&_svg]:size-4.5 @cred-md:h-[29px] @cred-lg:h-[30px] @cred-xl:h-[38px]"
          >
            <div className="relative">
              <MessageSquare />
              {hasComments && (
                <div className="absolute -top-1 -right-1 bg-[#1E3A8A] rounded-full size-2.5 @cred-md:size-3 @cred-lg:size-3.5 @cred-xl:size-[15px] flex items-center justify-center">
                  <span className="text-[8px] @cred-md:text-[9px] @cred-lg:text-[10px] @cred-xl:text-[11px] font-medium leading-tight text-white">
                    {article.aiGeneratedComments?.length}
                  </span>
                </div>
              )}
            </div>
            Comments
            <ChevronRight className="absolute right-1.5 top-1/2 size-4 -translate-y-1/2" />
          </Button> */}
          {/* <CommentsBottomSheet
            open={isCommentsOpen}
            setOpen={setIsCommentsOpen}
            article={article}
          /> */}
        </div>
      </div>
    );
  },
);

ActionButtons.displayName = 'ActionButtons';

export const ArticleCarouselItem: React.FC<ArticleCarouselItemProps> = ({
  index,
  article,
  metrics,
  selected,
  onSelect,
  isHackerNews,
  // TODO: uncomment once we have comments fully implemented
  // shouldOpenComments = false,
}) => {
  const {
    handleShare,
    isSharingUrlPending,
    isSharingUrlSuccess,
    isSharingUrlCopied,
    setIsSharingUrlCopied,
  } = useArticleShare();

  const [isTitleTooltipOpen, setIsTitleTooltipOpen] = useState(false);

  const titleTooltipRef = useRef<HTMLSpanElement>(null);

  const credibilityScore = useMemo(
    () => Math.round(article.metrics?.credibilityScore || 0),
    [article.metrics?.credibilityScore],
  );

  const chartData = useMemo(
    () => [
      {
        type: 'credible',
        score: credibilityScore,
        fill: 'hsl(var(--facts-pie-chart))',
      },
    ],
    [credibilityScore],
  );

  const endAngle = useMemo(
    () =>
      credibilityScore
        ? CHART_START_ANGLE + credibilityScore * CHART_ANGLE_MULTIPLIER
        : CHART_START_ANGLE,
    [credibilityScore],
  );

  const isSelected = selected === index;

  const handleTitleTooltipOpenChange = useCallback((open: boolean) => {
    if (titleTooltipRef.current) {
      const isClamped =
        titleTooltipRef.current.scrollHeight >
        titleTooltipRef.current.clientHeight;
      setIsTitleTooltipOpen(open && isClamped);
    }
  }, []);

  const handleSelect = useCallback(() => {
    onSelect(index);
    // `noopener`: the publisher page must not get a handle on this window
    // (it used to be the sidepanel iframe's; it is now the app's own tab).
    window.open(article.url, '_blank', 'noopener');
  }, [onSelect, index, article.url]);

  useEffect(() => {
    if (isSharingUrlSuccess) {
      setIsSharingUrlCopied(true);
      const timeout = setTimeout(() => {
        setIsSharingUrlCopied(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isSharingUrlSuccess, setIsSharingUrlCopied]);

  return (
    <div
      id={`embla__slide__content__${index}`}
      onClick={handleSelect}
      className={cn(
        'size-full cursor-pointer overflow-hidden rounded-md transition-colors duration-200 @cred-md:rounded-lg @cred-xl:rounded-[10px] pt-2 @cred-md:pt-[9.33px] @cred-xl:pt-[13.33px]',
        isSelected
          ? 'bg-neutral-1 hover:bg-neutral-1'
          : 'bg-silver-1 hover:bg-silver-1',
      )}
    >
      <div className="size-full flex flex-col space-y-1.5 @cred-md:space-y-[7px] @cred-lg:space-y-[8.24px] @cred-xl:space-y-2.5">
        <ArticleHeader
          brand={article.brand}
          publishedAt={article.publishedAt}
          createdAt={article.createdAt}
          isHackerNews={isHackerNews}
          isSharingUrlPending={isSharingUrlPending}
          isSharingUrlCopied={isSharingUrlCopied}
          handleShare={() => {
            handleShare({
              articleId: article.id,
              shareUrl: article.sharingUrl,
            });
            // Typed track() per doctrine §6 (was a raw posthog.capture with
            // unregistered props). `location` is a PLACEMENT, so it maps to
            // surface_detail — never `surface`, which is the boot origin
            // super-prop (§4 host-conflation guard).
            track('share_article', {
              article_id: String(article.id),
              surface_detail: 'article_analysis',
            });
          }}
        />
        <Separator className="h-[.5008px] bg-[#C0C0C0] mx-2 @cred-md:mx-[9.33px]" />
        <div className="flex w-full items-center justify-between px-2 @cred-md:px-[9.33px] @cred-xl:px-[13.33px] gap-1.5 @cred-xl:gap-2.5">
          <TooltipProvider>
            <Tooltip
              delayDuration={TOOLTIP_DELAY_DURATION}
              open={isTitleTooltipOpen}
              onOpenChange={handleTitleTooltipOpenChange}
            >
              <TooltipTrigger asChild>
                <span
                  ref={titleTooltipRef}
                  className="line-clamp-4 whitespace-break-spaces text-left text-xs font-bold leading-[13px] text-primary-foreground @cred-md:text-[15px] @cred-md:leading-[17.5px] @cred-lg:text-base @cred-lg:leading-[19px] @cred-xl:text-xl @cred-xl:leading-[25px] hover:underline"
                >
                  {article.title}
                </span>
              </TooltipTrigger>
              <AnalysisTooltipContent className="z-200 max-w-72 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] @cred-md:max-w-80 @cred-lg:max-w-[330px] @cred-xl:max-w-[440px]">
                <p className="text-xs leading-[13px] text-[#292929] @cred-md:text-[15px] @cred-md:leading-[17.5px] @cred-lg:text-base @cred-lg:leading-[19px] @cred-xl:text-xl @cred-xl:leading-[25px]">
                  {article.title}
                </p>
              </AnalysisTooltipContent>
            </Tooltip>
          </TooltipProvider>
          {!!metrics && (
            <CredibilityChart
              chartData={chartData}
              endAngle={endAngle}
              selected={selected}
              index={index}
            />
          )}
        </div>
        {/* <ActionButtons
          article={article}
          isSharingUrlPending={isSharingUrlPending}
          isSharingUrlCopied={isSharingUrlCopied}
          handleShare={() =>
            handleShare({
              articleId: article.id,
              shareUrl: article.sharingUrl,
            })
          }
          shouldOpenComments={shouldOpenComments}
        /> */}
        <Button
          onClick={handleSelect}
          className="mt-auto! h-[22px] w-full rounded-b-sm rounded-t-none bg-facts-pie-chart p-0 text-[10px] font-medium leading-[15.3504px] text-foreground hover:bg-cta @cred-md:text-[11px] @cred-md:leading-4 @cred-lg:text-[13px] @cred-lg:leading-[17px] @cred-xl:text-sm @cred-xl:leading-[19px] [&_svg]:size-3 @cred-md:[&_svg]:size-3.5 @cred-lg:[&_svg]:size-4 @cred-xl:[&_svg]:size-4.5 @cred-md:h-[24px] @cred-lg:h-[26px] @cred-xl:h-[34px]"
        >
          <ExternalLink />
          Go to this article
        </Button>
      </div>
    </div>
  );
};
